import { createContext, useContext, useState, type ComponentProps, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Janela, JanelaDeAcao } from '@/componentes/JanelaDeAcao';

/**
 * O rodapé fixo da janela (R01, p.29), preenchido DE DENTRO do formulário.
 *
 * A `Janela` já tem o rodapé: a prop `acoes` fica fora do corpo que rola, com
 * o fundo rebaixado do "Emitir folha, corrigido". Mas os botões de cada
 * formulário do combustível dependem do estado DELE — "Gravando…", desligado
 * enquanto envia, desligado sem máquina escolhida —, e esse estado nasce e
 * morre com o formulário, que só é montado com a janela aberta (é o que faz a
 * janela reabrir limpa). Subir tudo para a página para poder passar `acoes`
 * seria trocar oito formulários de lugar.
 *
 * Então a janela recebe no rodapé um ALVO vazio, e o formulário desenha os
 * botões nele (`AcoesNoRodape`, por portal). Fora do `<form>` no documento, o
 * botão de enviar se liga a ele pelo atributo `form` — e o Enter num campo
 * continua enviando, porque o botão é do formulário mesmo fora dele.
 */

const ContextoDoRodape = createContext<HTMLElement | null>(null);

/** O alvo que vai na prop `acoes` da janela: some na caixa do rodapé, que já é flex. */
function useAlvoDoRodape() {
  const [alvo, setAlvo] = useState<HTMLElement | null>(null);
  return { alvo, acoes: <span ref={setAlvo} className="contents" /> };
}

/** A `Janela` comum, com o rodapé à espera dos botões do formulário. */
export function JanelaComRodape({ children, ...resto }: Omit<ComponentProps<typeof Janela>, 'acoes'>) {
  const { alvo, acoes } = useAlvoDoRodape();
  return (
    <Janela {...resto} acoes={acoes}>
      <ContextoDoRodape.Provider value={alvo}>{children}</ContextoDoRodape.Provider>
    </Janela>
  );
}

/** A `JanelaDeAcao` comum (botão que abre + janela), com o mesmo rodapé. */
export function JanelaDeAcaoComRodape({ children, ...resto }: Omit<ComponentProps<typeof JanelaDeAcao>, 'acoes'>) {
  const { alvo, acoes } = useAlvoDoRodape();
  return (
    <JanelaDeAcao {...resto} acoes={acoes}>
      <ContextoDoRodape.Provider value={alvo}>{children}</ContextoDoRodape.Provider>
    </JanelaDeAcao>
  );
}

/**
 * Os botões do formulário, desenhados no rodapé fixo da janela — na ordem da
 * p.29: a saída à esquerda, a ação principal à direita. O de enviar leva
 * `form={id do formulário}`.
 */
export function AcoesNoRodape({ children }: { readonly children: ReactNode }) {
  const alvo = useContext(ContextoDoRodape);
  return alvo === null ? null : createPortal(children, alvo);
}
